import { FC } from 'react';
import { Root, Trigger, Portal, Content } from '@radix-ui/react-popover';
import styled from 'styled-components';
import tw from 'twin.macro';

import { FilterTagItem } from '../types/dataTypes';
import { ButtonColor, ButtonSize, Icons } from '../enums';
import { Button, Checkbox, Tag } from '../components';

const StyledTagContainer = styled.div`
  ${tw`flex flex-col h-full justify-between w-full`}
`;

const Tags = styled.div`
  ${tw`flex flex-wrap items-start justify-start mb-2 gap-0.5 min-h-[45px] w-full`}
`;

const StyledContent = styled(Content)`
  ${tw`rounded-xl w-64 backdrop-blur bg-white/50 border shadow-2xl border-gray-400 will-change-[transform,opacity]`};
  z-index: 10002;
`;

const Container = styled.div`
  ${tw`flex flex-col gap-2 p-2`};
`;

interface Props {
  tagItem: FilterTagItem;
  selectedItems: string[] | null;
  handleValueChange: (id: string, values: string[]) => void;
}

const TagContainer: FC<Props> = ({
  tagItem,
  selectedItems,
  handleValueChange,
}) => {
  const handleCheckboxClick = (id: string) => {
    let values;
    if (selectedItems && selectedItems.length > 0) {
      values = selectedItems.includes(id)
        ? selectedItems.filter((itemId) => itemId !== id)
        : [...selectedItems, id];
    } else values = [id];

    handleValueChange(tagItem.id, values);
  };

  const handleRemoveTag = (id: string) => {
    if (selectedItems) {
      const values = selectedItems.filter((itemId) => itemId !== id);
      handleValueChange(tagItem.id, values);
    }
  };

  return (
    <StyledTagContainer>
      <Tags>
        {selectedItems &&
          selectedItems?.length > 0 &&
          tagItem.items.map(
            ({ id, label, color }) =>
              selectedItems.includes(id) && (
                <Tag
                  key={id}
                  id={id}
                  label={[String(label)]}
                  color={color}
                  handleClick={handleRemoveTag}
                />
              )
          )}
      </Tags>
      <Root>
        <Trigger asChild>
          <div>
            <Button
              icon={Icons.plus}
              color={ButtonColor.primary}
              size={ButtonSize.small}
            />
          </div>
        </Trigger>
        <Portal>
          <StyledContent align="center" sideOffset={5}>
            <Container>
              {tagItem.items.map(({ id, label }) => (
                <Checkbox
                  id={id}
                  key={label}
                  label={label}
                  isChecked={selectedItems ? selectedItems.includes(id) : false}
                  onToggle={handleCheckboxClick}
                />
              ))}
            </Container>
          </StyledContent>
        </Portal>
      </Root>
    </StyledTagContainer>
  );
};

export default TagContainer;
